import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useCreateAttributeGroup, useUpdateAttributeGroup } from '../../hooks';
import type { AttributeGroup, CatalogLanguage } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { LocalizedFieldsEditor } from './LocalizedFieldsEditor';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';

interface AttributeGroupFormDialogProps {
  open: boolean;
  /** The group being edited (full detail — the list endpoint already returns it), or `null` to create. */
  editing: AttributeGroup | null;
  languages: CatalogLanguage[];
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Create / edit an attribute group — the display grouping an attribute's `groupCode` references
 * (plan-catalog-009, user-story-catalog-024). Mirrors `MeasurementFamilyFormDialog`.
 */
export function AttributeGroupFormDialog({
  open,
  editing,
  languages,
  canManage,
  onClose,
  onResult,
}: AttributeGroupFormDialogProps) {
  const { t } = useTranslation('catalog');
  const create = useCreateAttributeGroup();
  const update = useUpdateAttributeGroup();
  const isEdit = editing !== null;
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState('0');
  const [labelValues, setLabelValues] = useState<LocalizedFieldValue[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setActive(editing?.active ?? true);
    setSortOrder(editing?.sortOrder !== undefined ? String(editing.sortOrder) : '0');
    setLabelValues(
      (editing?.translations ?? []).map((translation) => ({
        locale: translation.locale,
        text: translation.label,
      })),
    );
  }, [open, editing]);

  const defaultLanguage = useMemo(() => languages.find((l) => l.defaultLocale), [languages]);
  const defaultLabelFilled =
    !defaultLanguage ||
    labelValues.some((v) => v.locale === defaultLanguage.locale && v.text.trim() !== '');
  const valid = code.trim() !== '' && defaultLabelFilled;

  const submit = () => {
    if (!valid) return;
    const translations = labelValues.map((v) => ({ locale: v.locale, label: v.text }));
    const parsedSortOrder = sortOrder.trim() === '' ? undefined : Number(sortOrder);
    const handlers = {
      onSuccess: () => {
        onResult(
          'success',
          isEdit ? t('attributeGroupFormDialog.saved') : t('attributeGroupFormDialog.created'),
        );
        onClose();
      },
      onError: (error: NormalizedApiError) => {
        if (error.code === 'ATTRIBUTE_GROUP_IN_USE') {
          onResult('error', t('attributeGroupFormDialog.errors.inUse'));
          return;
        }
        onResult('error', error.message);
      },
    };
    if (isEdit && editing) {
      update.mutate(
        { code: editing.code, body: { active, sortOrder: parsedSortOrder, translations } },
        handlers,
      );
      return;
    }
    create.mutate({ code: code.trim(), sortOrder: parsedSortOrder, translations }, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('attributeGroupFormDialog.editTitle') : t('attributeGroupFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label={t('attributeGroupFormDialog.codeLabel')}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={code.trim() === ''}
              disabled={isEdit || !canManage}
              autoFocus={!isEdit}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            {isEdit ? (
              <FormControlLabel
                control={
                  <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canManage} />
                }
                label={t('attributeGroupFormDialog.activeLabel')}
                sx={{ flexShrink: 0 }}
              />
            ) : null}
          </Stack>

          <TextField
            label={t('attributeGroupFormDialog.sortOrderLabel')}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            disabled={!canManage}
            fullWidth
            slotProps={{ htmlInput: { step: 1 } }}
          />

          <LocalizedFieldsEditor
            languages={languages}
            values={labelValues}
            onChange={setLabelValues}
            fieldLabel={t('attributeGroupFormDialog.labelFieldLabel')}
            disabled={!canManage}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending || !canManage}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
